import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import type { Editor } from '@tiptap/core';

import { preventRichTextToolbarMouseDown } from './richTextToolbar';

// Raw hexes in this dialog (#D32F2F, #4183C4, #666) have no designTokens
// equivalents yet — they await an editor-chrome palette decision (CONV-6).
function isValidUrl(url: string): boolean {
  const trimmed = url.trim();
  return /^https?:\/\/.+/i.test(trimmed) || /^mailto:[^\s@]+@[^\s@]+\.[^\s@]+/i.test(trimmed);
}

interface RichTextLinkDialogProps {
  editor: Editor;
  buttonStyle?: CSSProperties;
  activeButtonStyle?: CSSProperties;
}

export function RichTextLinkDialog({
  editor,
  buttonStyle,
  activeButtonStyle,
}: RichTextLinkDialogProps): ReactNode {
  const [isOpen, setIsOpen] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [validationError, setValidationError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isActive = editor.isActive('link');

  const currentHref = isActive
    ? (editor.getAttributes('link') as Record<string, unknown>)['href']
    : undefined;

  const openDialog = useCallback(() => {
    setUrlInput(typeof currentHref === 'string' ? currentHref : '');
    setValidationError('');
    setIsOpen(true);
  }, [currentHref]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setUrlInput('');
    setValidationError('');
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        handleClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, handleClose]);

  const applyLink = useCallback(() => {
    const trimmed = urlInput.trim();
    if (!trimmed) {
      handleClose();
      return;
    }
    if (!isValidUrl(trimmed)) {
      setValidationError('URL must start with http://, https://, or mailto:');
      return;
    }
    const isMailto = /^mailto:/i.test(trimmed);
    editor
      .chain()
      .focus()
      .setLink({ href: trimmed, target: isMailto ? null : '_blank' })
      .run();
    setIsOpen(false);
    setUrlInput('');
    setValidationError('');
  }, [editor, urlInput, handleClose]);

  const removeLink = useCallback(() => {
    editor.chain().focus().unsetLink().run();
    setIsOpen(false);
    setUrlInput('');
    setValidationError('');
  }, [editor]);

  // Position popover using fixed positioning to avoid clipping
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setPopoverPos({
        top: rect.bottom + 4,
        left: rect.left,
      });
    }
  }, [isOpen]);

  const popoverStyle: CSSProperties = {
    position: 'fixed',
    top: popoverPos.top,
    left: popoverPos.left,
    zIndex: 10000,
    padding: '10px',
    backgroundColor: '#fff',
    border: '1px solid rgba(0,0,0,0.15)',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
    minWidth: '300px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  };

  const inputStyle: CSSProperties = {
    flex: 1,
    height: '30px',
    padding: '0 8px',
    fontSize: '13px',
    border: `1px solid ${validationError ? '#D32F2F' : 'rgba(0,0,0,0.15)'}`,
    borderRadius: '4px',
    outline: 'none',
    fontFamily: 'inherit',
  };

  const smallButtonStyle: CSSProperties = {
    height: '30px',
    padding: '0 10px',
    fontSize: '12px',
    fontWeight: 600,
    border: '1px solid rgba(0,0,0,0.15)',
    borderRadius: '4px',
    backgroundColor: 'transparent',
    cursor: 'pointer',
  };

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative', display: 'inline-flex' }}
      onMouseDown={preventRichTextToolbarMouseDown}
    >
      <button
        type="button"
        onClick={openDialog}
        style={{
          ...(buttonStyle ?? {}),
          ...(isActive ? (activeButtonStyle ?? {}) : {}),
        }}
        title={isActive ? 'Edit link' : 'Insert link'}
      >
        🔗
      </button>

      {isOpen ? (
        <div
          style={popoverStyle}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Show current link URL if editing */}
          {isActive && typeof currentHref === 'string' ? (
            <div
              style={{
                fontSize: '11px',
                color: '#4183C4',
                wordBreak: 'break-all',
                marginBottom: '2px',
              }}
            >
              Current: {currentHref}
            </div>
          ) : null}

          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <input
              ref={inputRef}
              type="text"
              value={urlInput}
              onChange={(e) => {
                setUrlInput(e.target.value);
                setValidationError('');
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') {
                  applyLink();
                }
                if (e.key === 'Escape') {
                  handleClose();
                }
              }}
              placeholder="https://... or mailto:..."
              style={inputStyle}
              aria-label="Link URL"
            />
            <button
              type="button"
              onClick={applyLink}
              onMouseDown={(e) => e.stopPropagation()}
              style={smallButtonStyle}
            >
              Apply
            </button>
            {isActive ? (
              <button
                type="button"
                onClick={removeLink}
                onMouseDown={(e) => e.stopPropagation()}
                style={{ ...smallButtonStyle, color: '#D32F2F' }}
              >
                Remove
              </button>
            ) : null}
            <button
              type="button"
              onClick={handleClose}
              onMouseDown={(e) => e.stopPropagation()}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                color: '#666',
                padding: '0 2px',
                lineHeight: 1,
              }}
              title="Close"
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          {validationError ? (
            <div style={{ fontSize: '11px', color: '#D32F2F' }}>
              {validationError}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
